<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1 v-if="form.id">
        {{ $t('common.change') }} {{ landlord.name }}
      </h1>
      <h1 v-else>
        {{ $t('landlords.new_landlord') }}
      </h1>
    </div>
    <div class="details-component__content">
      <form class="form fixed-form">
        <table class="details-table tenant no-col-padding edit-table fixed-table label-col-wide-150">
          <tr
            class="details-table__row"
            :class="{'details-table__row_error' : errors.has('name')}"
          >
            <td class="details-table__col details-table__col_label">
              <label
                for="name"
                class="form__label"
              >{{ $t('common.landlord') }}</label>
            </td>
            <td class="details-table__col landlord_name">
              <input
                id="name"
                v-model.trim="form.name"
                v-validate="'required|min:3|max:255'"
                type="text"
                class="form__input"
                data-vv-as="Rentniku nimi"
                data-vv-validate-on="none"
                name="name"
              >
              <tooltip
                v-show="errors.has('name:required')"
                class="form__validation"
              >
                {{ $t('errors.name_required') }}
              </tooltip>
              <tooltip
                v-show="errors.has('name:min')"
                class="form__validation"
              >
                {{ $t('errors.name_min') }}
              </tooltip>
            </td>
          </tr>

          <tr
            class="details-table__row"
            :class="{'details-table__row_error' : errors.has('contactPerson')}"
          >
            <td class="details-table__col details-table__col_label">
              <label
                for="contactPerson"
                class="form__label"
              >{{ $t('add_tenant.contact_person') }}</label>
            </td>
            <td class="details-table__col">
              <input
                id="contactPerson"
                v-model.trim="form.contactPerson"
                v-validate
                type="text"
                class="form__input"
                data-vv-as="Kontaktisik"
                name="contactPerson"
              >
            </td>
          </tr>

          <tr
            class="details-table__row"
            :class="{'details-table__row_error' : errors.has('address')}"
          >
            <td class="details-table__col details-table__col_label">
              <label
                for="address"
                class="form__label"
              >{{ $t('common.address') }}</label>
            </td>
            <td class="details-table__col">
              <input
                id="address"
                v-model.trim="form.address"
                v-validate
                type="text"
                class="form__input"
                data-vv-rules="min:3|max:255"
                data-vv-as="Aadress"
                name="address"
              >
            </td>
          </tr>

          <tr
            class="details-table__row"
            :class="{'details-table__row_error' : errors.has('phone')}"
          >
            <td class="details-table__col details-table__col_label">
              <label
                for="phone"
                class="form__label"
              >{{ $t('common.phone') }}</label>
            </td>
            <td class="details-table__col">
              <input
                id="phone"
                v-model.trim="form.phoneNumber"
                v-validate="'validPhoneNr'"
                type="text"
                class="form__input"
                data-vv-validate-on="blur|focus"
                data-vv-as="Telefon"
                name="phone"
              >
              <tooltip
                v-show="errors.has('phone')"
                class="form__validation"
              >
                {{ $t('errors.phone_field') }}
              </tooltip>
            </td>
          </tr>

          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">
              {{ $t('tenant_details.since') }}
            </td>
            <td class="details-table__col">
              {{ landlord.createdAt }}
            </td>
          </tr>

          <tr
            class="details-table__row"
            :class="{'details-table__row_error' : errors.has('email')}"
          >
            <td class="details-table__col details-table__col_label">
              <label
                for="email"
                class="form__label"
              >{{ $t('common.email') }}</label>
            </td>
            <td class="details-table__col">
              <input
                id="email"
                v-model.trim="form.email"
                v-validate="'required|email|min:3|max:255'"
                type="email"
                class="form__input"
                data-vv-as="Email"
                name="email"
                data-vv-validate-on="blur"
              >
              <tooltip
                v-show="errors.has('email')"
                class="form__validation"
              >
                {{ $t('errors.email_field') }}
              </tooltip>
            </td>
          </tr>

          <tr
            class="details-table__row auto-height with-border"
            :class="{'details-table__row_error' : errors.has('zone')}"
          >
            <td class="details-table__col details-table__col_label">
              <label
                for="zones"
                class="form__label"
              >{{ $tc('common.zones', zones.data ? zones.data.length : 0) }}</label>
            </td>
            <td
              id="dropdown-zones"
              class="details-table__col txt-to-left"
              colspan="2"
            >
              <el-select
                v-model="checkedZones"
                class="fit-to-parent"
                multiple
                filterable
                remote
                :remote-method="fetchZones"
                :placeholder="$tc('common.zones', zones.data ? zones.data.length : 0)"
              >
                <el-option
                  v-for="zone in zones.data || $store.state.landlords.landlord.landlord.zones"
                  :key="zone.id"
                  :label="`${zone.code}`"
                  :value="zone.id"
                />
              </el-select>
              <tooltip
                v-show="errors.has('zone')"
                class="form__validation"
              >
                {{ $t('errors.zone_required') }}
              </tooltip>
            </td>
          </tr>

          <tr
            v-if="!form.isSuperAdmin"
            class="divider"
          >
            <td
              class="details-table__col txt-to-left"
              colspan="3"
            >
              <div class="details-component__header">
                <h3>{{ $t('common.advanced_settings') }}</h3>
              </div>
            </td>
          </tr>

          <tr
            v-if="!form.isSuperAdmin"
            class="details-table__row auto-height"
          >
            <td
              class="details-table__col txt-to-left padding-t-4"
              colspan="3"
            >
              <label class="form__label form__label__long no-hide with_checkbox">
                <input
                  id="can_exceed_capacity"
                  v-model="form.canChangeGuestParkingPrice"
                  type="checkbox"
                  name="can_exceed_capacity"
                  class="form__fieldset__checkbox"
                >
                <span
                  v-model="form.canChangeGuestParkingPrice"
                  class="form__fieldset__custom-checkbox"
                />
                <span class="custom-checkbox-label">{{ $t('landlord.can_change_guest_parking_price') }}</span>
              </label>
            </td>
          </tr>
          <tr
            v-if="!form.isSuperAdmin"
            class="details-table__row auto-height"
          >
            <td
              class="details-table__col txt-to-left padding-t-4"
              colspan="3"
            >
              <label class="form__label form__label__long no-hide with_checkbox">
                <input
                  id="can_edit_kiosk_permits"
                  v-model="form.canEditKioskPermits"
                  type="checkbox"
                  name="can_edit_kiosk_permits"
                  class="form__fieldset__checkbox"
                >
                <span
                  v-model="form.canEditKioskPermits"
                  class="form__fieldset__custom-checkbox"
                />
                <span class="custom-checkbox-label">{{ $t('landlord.can_edit_kiosk_permits') }}</span>
              </label>
            </td>
          </tr>
          <tr
            class="details-table__row auto-height"
          >
            <td
              class="details-table__col txt-to-left padding-t-4"
              colspan="3"
            >
              <label class="form__label form__label__long no-hide with_checkbox">
                <input
                  id="scoroEnabled"
                  v-model="form.scoroEnabled"
                  type="checkbox"
                  name="scoroEnabled"
                  class="form__fieldset__checkbox"
                >
                <span
                  v-model="form.scoroEnabled"
                  class="form__fieldset__custom-checkbox"
                />
                <span class="custom-checkbox-label">{{ $t('landlord.scoro_enabled') }}</span>
              </label>
            </td>
          </tr>
          <tr
            class="details-table__row auto-height"
          >
            <td class="details-table__col details-table__col_label">
              <label
                for="contactPerson"
                class="form__label"
              >{{ $t('landlord.landlord_theme') }}</label>
            </td>
            <td class="details-table__col">
              <input
                id="landlordTheme"
                v-model.trim="form.landlordTheme"
                type="text"
                class="form__input"
                data-vv-as="Theme"
                name="landlordTheme"
              >
            </td>
          </tr>
        </table>

        <div class="form__buttons buttonset">
          <router-link
            v-if="isNew"
            :to="{ name: 'landlords' }"
            class="buttonset__button btn btn--secondary"
          >
            {{ $t('common.discard') }}
          </router-link>
          <router-link
            v-else
            :to="{ name: 'landlord', params: { id: $route.params.id} }"
            class="buttonset__button btn btn--secondary"
          >
            {{ $t('common.discard') }}
          </router-link>
          <button
            type="submit"
            class="buttonset__button btn btn--main"
            @click.prevent.stop="onSubmit()"
          >
            {{ $t('common.save') }}
          </button>
        </div>

        <WarningModal
          v-if="landlordError"
          @close="closeWarningModal"
        >
          <h1 slot="header">
            {{ $t('common.error') }}
          </h1>
          <p slot="body">
            {{ $t('landlords.save_error') }}
          </p>
        </WarningModal>
      </form>
    </div>
  </div>
</template>

<script>
import { parse, format, isValidNumber } from 'libphonenumber-js';
import { mapGetters } from 'vuex';
import 'styles/blocks/details.scss';
import * as mTypes from 'store/mutation-types';
import moment from 'moment';
import Autocomplete from 'common/Autocomplete';
import Tooltip from 'common/Tooltip';
import WarningModal from 'common/WarningModal';

export default {
  components: {
    Autocomplete,
    Tooltip,
    WarningModal,
  },
  watch: {
    $route: 'fetchAllData',
  },
  computed: {
    isNew() {
      return !this.$route.params.id;
    },
    ...mapGetters({
      landlord: 'landlords/landlord/landlord',
      user: 'user/user/user',
      landlordError: 'landlords/landlord/error',
      zones: 'landlord/zones/zones',
    }),
  },
  data() {
    return {
      form: {},
      checkedZones: [],
    };
  },
  mounted() {
    this.fetchAllData();
  },
  created() {
    this.$store.commit(`landlords/landlord/${mTypes.CLEAR_STATE}`);
    this.$validator.remove('validPhoneNr');
    this.$validator.extend('validPhoneNr', {
      getMessage: () => 'Please insert valid phone number. Mobile number must include country code.',
      validate: (value) => {
        if (value.trim() === '') {
          return true;
        }
        const parsed = parse(value.trim(), { country: { default: 'EE' } });
        return isValidNumber(parsed);
      },
    });
    this.fetchAllData();
  },
  methods: {
    fetchAllData() {
      if (this.$route.params.id) {
        this.fetchLandlord();
      } else {
        this.$store.commit(`landlords/landlord/${mTypes.CLEAR_STATE}`);
        this.form = this.landlord;
        this.checkedZones = [];
        this.landlord.createdAt = moment(new Date()).format('DD.MM.YYYY');
        this.$validator.clean();
      }
    },
    fetchLandlord() {
      this.$store.dispatch('landlords/landlord/fetch', { id: this.$route.params.id, user: this.user, isSuperAdmin: this.isSuperAdmin() }).then(() => {
        this.form = this.landlord;
        this.checkedZones = [];
        if (this.landlord.zones) {
          this.landlord.zones.forEach((el) => {
            this.checkedZones.push(el.id);
          });
        }
        this.formatMobileNrForForm();
      });
    },
    fetchZones(query) {
      this.$store.dispatch('landlord/zones/fetchAll', { keyword: query, user: this.user });
    },
    onSubmit() {
      this.$store.commit(`landlords/landlord/${mTypes.DATA_ERROR}`, null);
      this.$validator.validateAll()
        .then((success) => {
          if (!success) {
            return;
          }
          this.parsePhoneBeforeSave();
          this.form.zones = [];
          this.checkedZones.forEach((el) => {
            this.form.zones.push({ id: el });
          });
          if (!this.form.canChangeGuestParkingPrice) {
            this.form.canChangeGuestParkingPrice = false;
          }
          if (!this.form.canEditKioskPermits) {
            this.form.canEditKioskPermits = false;
          }
          if (!this.form.scoroEnabled) {
            this.form.scoroEnabled = false;
          }
          this.$store.dispatch('landlords/landlord/saveLandlord', { landlord: this.form, user: this.user }).then(() => {
            if (!this.landlordError) {
              this.form = {};
              this.$store.commit(`landlord/landlords/${mTypes.EDIT_DATA}`, this.landlord);
              this.$router.push({ name: 'landlord', params: { id: this.landlord.id } });
            }
          }).catch(() => {
            this.$store.commit(`landlords/landlord/${mTypes.DATA_ERROR}`, true);
          });
          this.$validator.clean();
          this.$store.commit(`landlords/landlord/${mTypes.DATA_ERROR}`, null);
        }).catch(() => {
        });
    },
    parsePhoneBeforeSave() {
      const parsedPhone = parse(this.form.phoneNumber, { country: { default: 'EE' } });
      this.form.phoneCountry = parsedPhone.country;
      this.form.phone = parsedPhone.phone;
    },
    formatMobileNrForForm() {
      if (this.form.phone) {
        this.form.phoneNumber = format({ country: this.form.phoneCountry, phone: this.form.phone.toString() }, 'International_plaintext');
      } else {
        this.form.phoneNumber = '';
      }
    },
    closeWarningModal() {
      this.$store.commit(`landlords/landlord/${mTypes.DATA_ERROR}`, null);
    },
  },
};
</script>

<style lang="scss" scoped>

  .landlord_name input {
    font-size: 24px !important;
    font-family: 'Geogrotesque Md', sans-serif !important;
    padding-top: 12px !important;
  }

</style>

<style lang="scss">
#dropdown-zones .el-select__tags {
  position: static!important;
  transform: translateY(0)!important;
}
#dropdown-zones .el-select .el-input {
  height: 0px;
  visibility: hidden;
}
#dropdown-zones .el-select.fit-to-parent {
  padding: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/landlords/AddLandlordView.vue